import { useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { IAsset } from '../../services/assetServices';
import {
  IMetadata,
  getEntitiesMetadatas,
  getMetadata,
} from '../../services/metadataService';
import {
  createWork,
  updateWork,
  getWork,
  IRelatedAsset,
  IWorkStatus,
  getWorkStatuses,
} from '../../services/workService';
import ZoneSelector from '../zones/ZoneSelector';
import {
  FontIcon,
  IButtonProps,
  IconButton,
  Pivot,
  PivotItem,
  Stack,
  TooltipHost,
} from '@fluentui/react';
import AssetSelector from './AssetSelector';
import { useBoolean } from '@fluentui/react-hooks';
import IssueSelector from './IssueSelector';
import { Field, Input, makeStyles } from '@fluentui/react-components';
import WorkComments from './WorkComments';
import WorkMap from './WorkMap';
import WorkPhotos from './WorkPhotos';
import WorkReport from './WorkReport';
import {
  EditItem,
  WorkAvailability,
  WorkOfferDetails,
  WorkOffersList,
  WorkTypeField,
  useTenantWorkTypes,
} from '@moko/ui';
import OtherInfo from '../../common/OtherInfoPortal';
import WorkAssets from './WorkAssets';
import {
  acceptWorkOffer,
  createWorkAvailabilityWindow,
  deleteWorkAvailabilityWindow,
  getWorkAvailabilityWindow,
  getWorkAvailabilityWindows,
  getWorkOffer,
  getWorkOffers,
  rejectWorkOffer,
  updateWorkAvailabilityWindow,
  validate,
  type IValidationError,
  type IWorkOffer,
} from '@moko/utils';
import {
  FeatureFlag,
  Features,
  useFeatureFlag,
} from '../../libs/featureAccess';
import { useLocation } from 'react-router-dom';
import { getTenantWorkTypes } from '../../services/settingsService';

const FORM_FIELD_WIDTH = 460;

const useFormAlignStyles = makeStyles({
  // Force every horizontal Field in the form (EditItem-rendered fields and the
  // custom Zone/Asset/Issue selectors) to the exact same total width and label
  // column, so all labels and inputs line up on one grid with identical widths.
  alignedFields: {
    '& .fui-Field': {
      width: `${FORM_FIELD_WIDTH}px`,
      maxWidth: '100%',
      gridTemplateColumns: '140px 1fr',
    },
  },
});

const offersServices = {
  getWorkOffers,
  getWorkOffer,
  acceptWorkOffer,
  rejectWorkOffer,
};

const availabilityServices = {
  getWorkAvailabilityWindows,
  getWorkAvailabilityWindow,
  createWorkAvailabilityWindow,
  updateWorkAvailabilityWindow,
  deleteWorkAvailabilityWindow,
};

const Work = () => {
  const formAlign = useFormAlignStyles();
  const query = new URLSearchParams(useLocation().search);
  const routeWorkId = Number.parseInt(query.get('id') ?? '');
  const { isEnabled: showOffers } = useFeatureFlag(
    Features.FEATURE_WORK_OFFERS
  );
  const { isEnabled: showAvailability } = useFeatureFlag(
    Features.FEATURE_WORK_AVAILABILITY
  );
  const [selectedOfferId, setSelectedOfferId] = useState<number | null>(null);

  const isSaved = useRef(false);
  const initialRelatedAssetIdsRef = useRef<number[]>([]);
  const initialZoneIdRef = useRef<number | undefined>(undefined);
  const initialAssetIdRef = useRef<number | undefined>(undefined);
  const initialIssueIdRef = useRef<number | undefined>(undefined);
  const initialWorkOtherInfoRef = useRef<string>('{}');
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const [workStatuses, setWorkStatuses] = useState<IWorkStatus[]>([]);
  const [workId, setWorkId] = useState<number | undefined>(
    Number.isNaN(routeWorkId) ? undefined : routeWorkId
  );
  const [relatedAssetIds, setRelatedAssetIds] = useState<number[]>([]);
  const [relatedAssets, setRelatedAssets] = useState<IRelatedAsset[]>([]);
  const [issueId, setIssueId] = useState<number>();
  const [assetId, setAssetId] = useState<number>();
  const [assetName, setAssetName] = useState<string>();
  const [zoneId, setZoneId] = useState<number>();
  const [orderId, setOrderId] = useState<number>();
  const [workTypeId, setWorkTypeId] = useState<number | null>(null);
  const [savedWorkTypeId, setSavedWorkTypeId] = useState<number | null>(null);
  const [workTypeReady, setWorkTypeReady] = useState(Number.isNaN(routeWorkId));
  const workTypes = useTenantWorkTypes(undefined, true, getTenantWorkTypes);

  const [metadata, setMetadata] = useState<IMetadata>();
  const [metadataName, setMetadataName] = useState<string>();

  const [metadatas, setMetadatas] = useState<{ key: string; value: string }[]>(
    []
  );

  const [otherInfo, setOtherInfo] = useState<Record<string, unknown>>({});

  const [
    isIssueSelectorOpen,
    { setTrue: showIssueSelector, setFalse: hideIssueSelector },
  ] = useBoolean(false);

  useEffect(() => {
    const fetchData = async () => {
      const abortController = new AbortController();
      setIsInProgress(true);
      try {
        const workMetadatas = await getEntitiesMetadatas(abortController, [
          'Work',
        ]);
        const metadatas = Object.keys(workMetadatas?.metadatas || {}).map(
          (key) => ({ key: key, value: key.split('|')[1] })
        );
        setMetadatas(metadatas ?? []);
        setWorkStatuses(await getWorkStatuses(abortController));
      } catch (error: any) {
        console.error('Error:', error);
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
      return () => {
        abortController.abort();
      };
    };

    fetchData();
  }, []);

  const hasDraftRelatedAssetsChanges =
    !workId &&
    relatedAssetIds.join(',') !== initialRelatedAssetIdsRef.current.join(',');

  const hasWorkExternalChanges =
    zoneId !== initialZoneIdRef.current ||
    assetId !== initialAssetIdRef.current ||
    issueId !== initialIssueIdRef.current ||
    JSON.stringify(otherInfo ?? {}) !== initialWorkOtherInfoRef.current;

  const [errors, setErrors] = useState<IValidationError[]>();
  const companyInfoErrors = useMemo(
    () => validate(otherInfo, metadata?.validations ?? [], metadata?.fields),
    [otherInfo, metadata]
  );
  useEffect(() => {
    setErrors(companyInfoErrors);
  }, [companyInfoErrors]);
  const isCreateMode = !workId;
  const disabledTabTooltip = 'Available after the work is saved.';
  const disabledTabProps: IButtonProps | undefined = isCreateMode
    ? {
        disabled: true,
        style: { color: 'grey' },
      }
    : undefined;
  const renderDisabledTabLink = (
    link?: { headerText?: string },
    defaultRenderer?: (link: { headerText?: string }) => ReactNode
  ) => (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {defaultRenderer && link ? defaultRenderer(link) : link?.headerText}
      <TooltipHost content={disabledTabTooltip}>
        <FontIcon iconName="Info" style={{ color: '#605e5c' }} />
      </TooltipHost>
    </span>
  );

  if (selectedOfferId && workId && showOffers) {
    return (
      <WorkOfferDetails
        workOfferId={selectedOfferId}
        services={offersServices}
        onBack={() => setSelectedOfferId(null)}
        setIsInProgress={setIsInProgress}
        setErrorMessage={setErrorMessage}
        setSuccessMessage={setSuccessMessage}
      />
    );
  }

  return (
    <>
      <Pivot>
        <PivotItem headerText="Details" alwaysRender={true}>
          <Stack>
            <div className={formAlign.alignedFields}>
              <EditItem
                headerSlot={
                  <WorkTypeField
                    mode={workId ? 'read' : 'create'}
                    options={workTypes.options}
                    value={workId ? savedWorkTypeId : workTypeId}
                    onChange={setWorkTypeId}
                    loading={workTypes.loading || (!!workId && !workTypeReady)}
                    error={
                      workTypes.error ? 'Could not load work types.' : null
                    }
                    onRetry={workTypes.refetch}
                  />
                }
                getAction={async (abortController, id) => {
                  const work: any = await getWork(abortController, id);
                  const metadataName = work.metadataName
                    ? work.metadataName.split('|')[1]
                    : undefined;
                  setMetadataName(metadataName);
                  const metadata = await getMetadata(
                    abortController,
                    'Work',
                    undefined,
                    metadataName
                  );
                  setMetadata(metadata);
                  if (!id) {
                    return { otherInfo: '{}' };
                  }
                  setWorkId(id);
                  setZoneId(work.zoneId);
                  setOrderId(work.orderId);
                  initialZoneIdRef.current = work.zoneId;
                  setIssueId(work.issueId);
                  initialIssueIdRef.current = work.issueId;
                  setAssetId(work.assetId);
                  initialAssetIdRef.current = work.assetId;
                  setAssetName(work.assetCodeAndName);
                  const currentRelatedAssets = Array.isArray(work.relatedAssets)
                    ? work.relatedAssets
                    : [];
                  const currentRelatedAssetIds = currentRelatedAssets.map(
                    (asset: IRelatedAsset) => asset.id
                  );
                  setRelatedAssets(currentRelatedAssets);
                  setRelatedAssetIds(currentRelatedAssetIds);
                  initialRelatedAssetIdsRef.current = currentRelatedAssetIds;
                  work.statusId = (work.statusId ?? '').toString();
                  setOtherInfo(work.otherInfo);
                  setSavedWorkTypeId(
                    typeof work.workTypeId === 'number' ? work.workTypeId : null
                  );
                  setWorkTypeReady(true);
                  initialWorkOtherInfoRef.current = JSON.stringify(
                    work.otherInfo ?? {}
                  );
                  return work;
                }}
                saveDisabled={companyInfoErrors.length > 0}
                newAction={async (abortController, newWork) => {
                  const otherInfoErrors = validate(
                    otherInfo,
                    metadata?.validations ?? [],
                    metadata?.fields
                  );
                  if (otherInfoErrors.length > 0) {
                    setErrors(otherInfoErrors);
                    return;
                  }
                  let newWorkResponse;
                  try {
                    newWorkResponse = await createWork(
                      abortController,
                      {
                        assetId: assetId,
                        relatedAssetIds: relatedAssetIds,
                        zoneId: zoneId,
                        description: newWork.description as string | undefined,
                        issueTypeId: newWork.issueTypeId as number | undefined,
                        statusId: newWork.statusId
                          ? Number.parseInt(newWork.statusId as string)
                          : undefined,
                        otherInfo,
                        metadataName: `Work|${metadataName}`,
                        workTypeId,
                      },
                      issueId
                    );
                  } catch (error) {
                    setErrorMessage(
                      (error as { message?: string })?.message ||
                        'Create work failed.'
                    );
                    throw error;
                  }
                  setWorkId(newWorkResponse.workId);
                  initialRelatedAssetIdsRef.current = relatedAssetIds;
                  initialZoneIdRef.current = zoneId;
                  initialAssetIdRef.current = assetId;
                  initialIssueIdRef.current = issueId;
                  initialWorkOtherInfoRef.current = JSON.stringify(
                    otherInfo ?? {}
                  );
                  setSuccessMessage(
                    `Work with id: ${newWorkResponse.workId} created.`
                  );
                }}
                editAction={async (abortController, id, editedWork) => {
                  const otherInfoErrors = validate(
                    otherInfo,
                    metadata?.validations ?? [],
                    metadata?.fields
                  );
                  if (otherInfoErrors.length > 0) {
                    setErrors(otherInfoErrors);
                    return;
                  }
                  await updateWork(abortController, id, {
                    description: editedWork.description as string | undefined,
                    statusId: Number.parseInt(editedWork.statusId as string),
                    metadataName: `Work|${metadataName}`,
                    assetId: assetId,
                    zoneId: zoneId,
                    issueId: issueId,
                    otherInfo,
                  });
                  initialZoneIdRef.current = zoneId;
                  initialAssetIdRef.current = assetId;
                  initialIssueIdRef.current = issueId;
                  initialWorkOtherInfoRef.current = JSON.stringify(
                    otherInfo ?? {}
                  );
                  setSuccessMessage(
                    `Work with id: ${id} successfully updated.`
                  );
                }}
                isSaved={isSaved}
                isDirtyExternal={
                  hasDraftRelatedAssetsChanges ||
                  hasWorkExternalChanges ||
                  (!workId && workTypeId != null)
                }
                back={'/works'}
                callGetBeforeNew={false}
                metadata={{
                  fields: [
                    {
                      name: 'statusId',
                      fieldType: 'Lookup',
                      label: 'Status Id',
                      lookupList: 'Statuses',
                    },
                    {
                      name: 'description',
                      fieldType: 'String',
                      label: 'Description',
                      rows: 5,
                    },
                    {
                      name: 'orderId',
                      fieldType: 'Link',
                      label: 'Order',
                      options: {
                        showAsLink: true,
                        linkUrl: '/editOrder?id={id}',
                      },
                      effect: orderId ? 'Show' : 'Hide',
                    },
                    {
                      name: 'metadataName',
                      fieldType: 'Lookup',
                      label: 'Template',
                      lookupList: 'entitiesMetadatas',
                      effect: workId ? 'Readonly' : 'Show',
                    },
                  ],
                  lookups: [
                    {
                      name: 'Statuses',
                      values: workStatuses?.map((wos) => ({
                        key: wos.id.toString(),
                        value: wos.name,
                        icon: wos.icon,
                      })),
                    },
                    {
                      name: 'entitiesMetadatas',
                      values: metadatas,
                    },
                  ],
                  validations: [],
                  interactions: [],
                }}
                afterChange={async (item: any) => {
                  if (item.hasOwnProperty('metadataName')) {
                    const newName = item.metadataName.split('|')[1];
                    if (newName !== metadataName) {
                      setMetadataName(newName);
                      const abortController = new AbortController();
                      const md = await getMetadata(
                        abortController,
                        'Work',
                        undefined,
                        newName
                      );
                      setMetadata(md);
                    }
                  }
                }}
              >
                <Stack
                  tokens={{ childrenGap: 10 }}
                  styles={{ root: { width: '100%' } }}
                >
                  <FeatureFlag featureKey={Features.FEATURE_ZONES}>
                    <ZoneSelector
                      zoneId={zoneId}
                      disabled={isSaved.current}
                      onSelected={(id: number) => setZoneId(id)}
                    />
                  </FeatureFlag>
                  <FeatureFlag featureKey={Features.FEATURE_ASSETS}>
                    <AssetSelector
                      assetId={assetId}
                      assetName={assetName}
                      disabled={isSaved.current}
                      onSelected={(asset: IAsset) => {
                        setAssetId(asset.id);
                        setAssetName(`${asset.code}|${asset.name}`);
                      }}
                    />
                  </FeatureFlag>
                  <FeatureFlag featureKey={Features.FEATURE_ISSUES}>
                    <Field label="Issue Id" orientation="horizontal">
                      <div
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                          width: '100%',
                        }}
                      >
                        <Input
                          style={{ width: '100%' }}
                          value={issueId?.toString() || ''}
                          readOnly
                          onClick={showIssueSelector}
                        />
                        <IconButton
                          iconProps={{ iconName: 'Search' }}
                          styles={{
                            root: {
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              height: 28,
                            },
                          }}
                          onClick={() => showIssueSelector()}
                        />
                      </div>
                    </Field>
                    <IssueSelector
                      isOpen={isIssueSelectorOpen}
                      hideIssueSelector={hideIssueSelector}
                      onIssueLinkClick={(issueId) => {
                        setIssueId(issueId);
                        hideIssueSelector();
                      }}
                    />
                  </FeatureFlag>
                  <p style={{ height: '2px' }} />
                </Stack>
              </EditItem>
            </div>
            {metadata ? (
              <Stack tokens={{ childrenGap: 10 }}>
                <OtherInfo
                  metadata={metadata}
                  otherInfo={JSON.stringify(otherInfo)}
                  isSaved={isSaved}
                  onOtherInfoChanged={(newValue: any) => setOtherInfo(newValue)}
                  relatedEntities={relatedAssets.map((asset) => ({
                    id: asset.id,
                    entityInfo: `${asset.id}|${asset.code}|${asset.name}`,
                  }))}
                  validationErrors={errors}
                  onValidationErrorsChanged={(newErrors) =>
                    setErrors(newErrors)
                  }
                />
              </Stack>
            ) : (
              <></>
            )}
          </Stack>
        </PivotItem>
        <PivotItem
          headerText="Assets"
          headerButtonProps={disabledTabProps}
          onRenderItemLink={
            isCreateMode
              ? (link, defaultRenderer) =>
                  renderDisabledTabLink(link, defaultRenderer)
              : undefined
          }
        >
          <WorkAssets
            workId={workId}
            assetIds={relatedAssetIds}
            onAssetIdsChanged={setRelatedAssetIds}
          />
        </PivotItem>
        {showAvailability && (
          <PivotItem
            headerText="Availability"
            headerButtonProps={disabledTabProps}
            onRenderItemLink={
              isCreateMode
                ? (link, defaultRenderer) =>
                    renderDisabledTabLink(link, defaultRenderer)
                : undefined
            }
          >
            {workId != null && workId > 0 && (
              <WorkAvailability
                workId={workId}
                services={availabilityServices}
                setIsInProgress={setIsInProgress}
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
              />
            )}
          </PivotItem>
        )}
        <PivotItem
          headerText="Comments"
          headerButtonProps={disabledTabProps}
          onRenderItemLink={
            isCreateMode
              ? (link, defaultRenderer) =>
                  renderDisabledTabLink(link, defaultRenderer)
              : undefined
          }
        >
          <WorkComments workId={workId} />
        </PivotItem>
        <PivotItem
          headerText="Location"
          headerButtonProps={disabledTabProps}
          onRenderItemLink={
            isCreateMode
              ? (link, defaultRenderer) =>
                  renderDisabledTabLink(link, defaultRenderer)
              : undefined
          }
        >
          <WorkMap workId={workId} />
        </PivotItem>
        <PivotItem
          headerText="Photos"
          headerButtonProps={disabledTabProps}
          onRenderItemLink={
            isCreateMode
              ? (link, defaultRenderer) =>
                  renderDisabledTabLink(link, defaultRenderer)
              : undefined
          }
        >
          <WorkPhotos workId={workId} />
        </PivotItem>
        {workId && workId > 0 && showOffers && (
          <PivotItem headerText="Offers">
            <WorkOffersList
              workId={workId}
              services={offersServices}
              onOpenOffer={(offer: IWorkOffer) => setSelectedOfferId(offer.id)}
              setIsInProgress={setIsInProgress}
              setErrorMessage={setErrorMessage}
            />
          </PivotItem>
        )}
        {workId && workId > 0 && (
          <PivotItem headerText="Report">
            <WorkReport workId={workId} />
          </PivotItem>
        )}
      </Pivot>
    </>
  );
};

export default Work;
